<script>
import {
  AuthenticationDetails,
  CognitoUser,
} from 'amazon-cognito-identity-js';
import { mapGetters } from 'vuex';
import _ from 'lodash';

export default {
  name: 'cognito-authentication',
  computed: {
    ...mapGetters(['userPool']),
  },
  methods: {
    $validateSession({ skipRedirect } = {}) {
      return new Promise(async (resolve, reject) => {
        try {
          const session = await this.$restoreSession({ skipRedirect });
          if (session) {
            // if there is a session, set aws creds in state so this user can have
            // it's limited permissions
            const Creds = this.$aws.CognitoIdentityCredentials;
            const cogUrl = `cognito-idp.us-west-2.amazonaws.com/${process.env.VUE_APP_USERPOOL_ID}`;
            const params = {
              IdentityPoolId: process.env.VUE_APP_FED_ID,
              Logins: {
                [cogUrl]: session.getIdToken().getJwtToken(),
              },
            };
            this.$aws.config.credentials = new Creds(params, {
              region: process.env.VUE_APP_COGNITO_REGION,
            });
            await this.$aws.config.credentials.getPromise();
            this.$log.info('session set for: ', session.idToken.payload.email);
            this.$log.info('session aws credentials: ', this.$aws.config.credentials);
            resolve(session);
          } else if (!skipRedirect) {
            // route to login if there is no user session
            this.$router.push({ path: '/tablero-de-control' });
          }
          resolve(false);
        } catch (error) {
          reject(error.message || error);
        }
      });
    },
    $validSession() {
      const cognitoUser = this.userPool.getCurrentUser();
      return new Promise((resolve, reject) => {
        if (cognitoUser !== null) {
          // there is a saved session
          cognitoUser.getSession((err, session) => {
            if (err) {
              reject(err);
            }
            if (session && session.isValid()) {
              resolve(session);
            }
          });
        } else {
          // there is no session
          resolve(false);
        }
      });
    },
    async $checkSession() {
      // since sessions expire after an hour
      const $creds = this.$aws.config.credentials;
      if ($creds && $creds.needsRefresh()) {
        this.$log.warn('session needs refresh');
        await this.$validateSession();
      }
      const user = this.userPool.getCurrentUser();
      return user || {};
    },
    $restoreSession({ skipRedirect } = {}) {
      const cognitoUser = this.userPool.getCurrentUser();
      return new Promise((resolve, reject) => {
        if (cognitoUser !== null) {
          // there is a saved session
          cognitoUser.getSession((err, session) => {
            if (err) {
              // user was deleted and trying to reload so move them
              // to login
              if (['NotAuthorizedException', 'UserNotFoundException'].includes(err.code)) {
                if (err.code === 'UserNotFoundException') {
                  this.$clearAuthStorage();
                }
                if (!skipRedirect) {
                  this.$router.push({ path: '/tablero-de-control' });
                }
              }
              if (err.code === 'NetworkError') {
                this.$store.dispatch('setNetworkModal', true);
              }
              reject(err);
            }
            if (session && session.isValid()) {
              resolve(session);
            } else {
              resolve(false);
            }
          });
        } else {
          // there is no session
          resolve(false);
        }
      });
    },
    $cognitoLogin({ user, pass }) {
      const authData = {
        Username: user,
        Password: pass,
      };
      const authDetails = new AuthenticationDetails(authData);
      const userData = {
        Username: user,
        Pool: this.userPool,
      };
      const cognitoUser = new CognitoUser(userData);
      return new Promise((resolve, reject) => {
        cognitoUser.authenticateUser(authDetails, {
          onSuccess(result) {
            resolve(result);
          },
          onFailure(err) {
            reject(err);
          },
        });
      });
    },
    $resendNewUserCode({ email }) {
      const userData = {
        Username: email,
        Pool: this.userPool,
      };
      const cognitoUser = new CognitoUser(userData);
      return new Promise((resolve, reject) => {
        cognitoUser.resendConfirmationCode((err, result) => {
          if (err) {
            reject(err);
            return;
          }
          resolve(result);
        });
      });
    },
    $confirmNewUser({ code, user, pass }) {
      const userData = {
        Username: user,
        Pool: this.userPool,
      };

      const cognitoUser = new CognitoUser(userData);

      return new Promise((resolve, reject) => {
        cognitoUser.confirmRegistration(code, true, (err) => {
          if (err) {
            reject(err);
            return;
          }
          // if we want to authenticate user right after confirmation and
          // give them a session
          const authData = {
            Username: user,
            Password: pass,
          };
          const authDetails = new AuthenticationDetails(authData);
          cognitoUser.authenticateUser(authDetails, {
            onSuccess(result) {
              resolve(result);
            },
            onFailure(error) {
              reject(error);
            },
          });
        });
      });
    },
    $forgotPassword({ user }) {
      const userData = {
        Username: user,
        Pool: this.userPool,
      };

      const cognitoUser = new CognitoUser(userData);

      return new Promise((resolve, reject) => {
        cognitoUser.forgotPassword({
          onSuccess() {
            resolve();
          },
          onFailure(err) {
            reject(err);
          },
        });
      });
    },
    $changePassword({ oldPass, newPass }) {
      const cognitoUser = this.userPool.getCurrentUser();
      return new Promise((resolve, reject) => {
        if (cognitoUser !== null) {
          // there is a saved session
          cognitoUser.getSession(async (err, session) => {
            if (err) {
              reject(err);
            }
            if (session.isValid()) {
              cognitoUser.changePassword(oldPass, newPass, (err, result) => {
                if (err) {
                  reject(err);
                }
                resolve(result);
              });
            }
          });
        } else {
          // there is no session
          reject(new Error('No user session. Refresh page.'));
        }
      });
    },
    $submitResetPass({ code, pass, user }) {
      const userData = {
        Username: user,
        Pool: this.userPool,
      };

      const cognitoUser = new CognitoUser(userData);

      return new Promise((resolve, reject) => {
        cognitoUser.confirmPassword(code, pass, {
          onSuccess() {
            resolve(cognitoUser);
          },
          onFailure(err) {
            reject(err);
          },
        });
      });
    },
    $clearAuthStorage() {
      _.forOwn(window.localStorage, (value, key) => {
        if (key.match(/^CognitoIdentityServiceProvider/gi) || key.match(/^aws.cognito.identity/gi)) {
          window.localStorage.removeItem(key);
        }
      });
    },
    $logout() {
      const cognitoUser = this.userPool.getCurrentUser();
      return new Promise((resolve, reject) => {
        if (cognitoUser) {
          resolve(cognitoUser.signOut());
        } else {
          reject(new Error('could not log out user'));
        }
      });
    },
  },
};
</script>
